import React from "react";

import { Box, Sheet, Stack, Table, Theme } from "@mui/joy";

import { Cell } from "../shared/interfaces";

import { usePapaParse } from "react-papaparse";
import { TableVirtuoso } from "react-virtuoso";
import { convertReferencesToParts, ReferenceProcessor } from "../util/references";

import ReferenceLink from "./ReferenceLink";
import { MarvinSenderRow } from "./MarvinSenderRow";
import TableInformationRow from "./TableInformationRow";
import { downloadTableCellsAsCsv, getTableCellsAsTabularText } from "../util/dataUtil";
import { copyToClipboardOrShowAlert } from "../util/objectUtil";
import { AnalyticsEvents, analyticsEvent } from "../util/analytics";
import { MessageProps } from "../shared/props";

const styles = {
    topContainer: (theme: Theme) => ({
        border: "1px solid",
        borderColor: "neutral.outlinedBorder",
        borderRadius: theme.radius.sm,
        scrollbarColor: "var(--joy-palette-neutral-400) var(--joy-palette-neutral-50)",
    }),
    tableWrapper: { height: "600px" },
};

/**
 * Calculates column widths to make sure longer columns have a bit more room
 */
function calculateColumnWidths(data: string[][]): string[] {
    const MIN_WIDTH_PERCENTAGE = 20;
    const REFERENCE_TEXT_MIN_LENGTH = 100;

    const columnLengths = data[0].map((header, colIndex) => {
        return data.reduce((sum, row) => {
            const cell = row[colIndex] ?? "";

            // References get longer text later on so make sure we have enough room
            const cellLength = header.startsWith("Reference")
                ? Math.max(REFERENCE_TEXT_MIN_LENGTH, cell.length)
                : cell.length;
            return sum + cellLength;
        }, 0);
    });

    // Calculate the total length across all columns
    const totalLength = columnLengths.reduce((sum, length) => sum + length, 0);

    // Convert each column length to a percentage of the total length
    let columnPercentages = columnLengths.map((length) => (length / totalLength) * 100);

    // Apply minimum width and adjust remaining columns
    let adjustedPercentages = columnPercentages.map((percentage) =>
        percentage < MIN_WIDTH_PERCENTAGE ? MIN_WIDTH_PERCENTAGE : percentage,
    );

    // Calculate the sum of the adjusted percentages
    const adjustedTotal = adjustedPercentages.reduce((sum, percentage) => sum + percentage, 0);

    // Scale down percentages proportionally to fit within 100%
    return adjustedPercentages.map((percentage) => {
        const pct = (percentage / adjustedTotal) * 100;
        return pct > MIN_WIDTH_PERCENTAGE ? `${pct}%` : "auto";
    });
}

export default function TableMessage(props: MessageProps) {
    const msg = props.message;
    const setCurrentFileViewerFile = props.setCurrentFileViewerFile;
    const currentFileViewerFile = props.currentFileViewerFile;

    const { readString } = usePapaParse();

    // State to store parsed data
    const [parsedData, setParsedData] = React.useState<Cell[][]>([]);
    const [columnWidths, setColumnWidths] = React.useState<string[]>([]);

    // Parse the CSV and calculate column widths
    React.useEffect(() => {
        readString<string[]>(msg.message, {
            worker: true,
            complete: ({ data }) => {
                const calculatedColumnWidths = calculateColumnWidths(data);
                const transformedData: Cell[][] = data.map((row) =>
                    row.map(convertReferencesToParts),
                );

                setParsedData(transformedData);
                setColumnWidths(calculatedColumnWidths);
            },
        });
    }, [msg.message, readString]);

    const referenceProcessor = new ReferenceProcessor(msg);

    const getTableHeaderCell = () => (
        <tr>
            {parsedData[0].map((header: Cell, index: number) => (
                <th key={`header-${msg.id}-${index}`} style={{ width: `${columnWidths[index]}` }}>
                    {header[0].rawContent}
                </th>
            ))}
        </tr>
    );

    const getTableRow = (index: number, row: Cell[]) => {
        const getTableBodyCell = (cell: Cell, cellIndex: number) => {
            const rowKey = `cell-${msg.id}-${index}-${cellIndex}`;
            return (
                <td key={rowKey} style={{ whiteSpace: "pre-wrap" }}>
                    {cell.map((part, partIndex) => {
                        if (!part.isReference) {
                            return <>{part.rawContent}</>;
                        }

                        return (
                            <ReferenceLink
                                key={`${rowKey}-${partIndex}`}
                                referenceKey={part.rawContent}
                                processor={referenceProcessor}
                                currentFileViewerFile={currentFileViewerFile}
                                setCurrentFileViewerFile={setCurrentFileViewerFile}
                            />
                        );
                    })}
                </td>
            );
        };

        return row.map(getTableBodyCell);
    };

    const handleDownloadClick = () => {
        analyticsEvent(AnalyticsEvents.DOWNLOAD_SPREADSHEET);
        downloadTableCellsAsCsv(referenceProcessor, parsedData);
    };

    const handleCopyClick = () => {
        analyticsEvent(AnalyticsEvents.COPY_SPREADSHEET);
        const tabular = getTableCellsAsTabularText(referenceProcessor, parsedData);
        copyToClipboardOrShowAlert(tabular);
    };

    return (
        <Stack spacing={1}>
            {!props.singleMessageView && <MarvinSenderRow />}

            <Sheet sx={styles.topContainer}>
                <Stack>
                    <TableInformationRow
                        numberOfRows={parsedData.length - 1}
                        showDownloadCopy={true}
                        onDownloadClick={handleDownloadClick}
                        onCopyClick={handleCopyClick}
                    />
                    {parsedData.length !== 0 && (
                        <Box sx={styles.tableWrapper}>
                            <TableVirtuoso
                                data={parsedData.slice(1)}
                                components={{ Table: Table }}
                                overscan={50}
                                fixedHeaderContent={getTableHeaderCell}
                                itemContent={getTableRow}
                            />
                        </Box>
                    )}
                </Stack>
            </Sheet>
        </Stack>
    );
}
